import { useEffect, useState } from 'react';
import { CustomCakeData } from './CustomCakeBuilder';
import { useAppSelector } from '../../../../store';
import { mergeImages } from '../../../../utils/mergeImages';
import { getAssetURl } from '../../../../utils/url';
import { Shape } from '../../../../store/shape/api/interface';
import { Flavor } from '../../../../store/flavor/api/interface';
import { ToppingsCategoryItem } from '../../../../store/toppingsCategoryItem/api/interface';

interface UseCakePreviewImageParams {
  cake: CustomCakeData | null;
  shapes: Shape[];
  flavors: Flavor[];
  toppingItems: ToppingsCategoryItem[];
}

// Helper function to create a stable key from cake data
const getCakeKey = (cake: CustomCakeData | null): string | null => {
  if (!cake) return null;
  return `${cake.shapeId}-${cake.flavorId}-${cake.colorCode}-${cake.displayMode}-${cake.toppingId || 'no-topping'}`;
};

export const useCakePreviewImage = ({
  cake,
  shapes,
  flavors,
  toppingItems,
}: UseCakePreviewImageParams): { previewImage: string | null; isLoading: boolean } => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  
  const cakeKey = getCakeKey(cake);

  useEffect(() => {
    const generatePreview = async () => {
      if (!cake || !cakeKey) {
        setPreviewImage(null);
        return;
      }

      const shape = shapes.find((s) => s.id === cake.shapeId);
      const flavor = flavors.find((f) => f.id === cake.flavorId);

      if (!shape || !flavor || !cake.colorCode) {
        setPreviewImage(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        // Get the correct images based on displayMode
        const shapeImageUrl = cake.displayMode === 'front' 
          ? shape.frontImg 
          : shape.topImg;
        
        const shapeColorImageUrl = cake.displayMode === 'front'
          ? shape.frontColorImg
          : shape.topColorImg;

        // Find flavor image for selected shape
        const flavorShapeImage = flavor.shapeImages?.find(
          (img) => img.shapeId === shape.id
        );
        
        const flavorImageUrl = cake.displayMode === 'front'
          ? flavorShapeImage?.front
          : flavorShapeImage?.top;

        // Find topping image for selected shape
        let toppingImageUrl: string | undefined;
        if (cake.toppingId) {
          const selectedTopping = toppingItems.find((t) => t.id === cake.toppingId);
          if (selectedTopping) {
            const toppingShapeImage = selectedTopping.toppingShapeImages?.find(
              (img) => img.shapeId === shape.id
            );
            if (toppingShapeImage) {
              toppingImageUrl = cake.displayMode === 'front'
                ? toppingShapeImage.front
                : toppingShapeImage.top;
            } else {
              // Topping exists but no image for this shape
              console.warn(`Topping ${selectedTopping.id} (${selectedTopping.labelEn}) does not have an image for shape ${shape.id} (${shape.labelEn})`);
            }
          } else {
            // Topping ID exists but not found in Redux store
            console.warn(`Topping with ID ${cake.toppingId} not found in Redux store. Available toppings: ${toppingItems.map(t => t.id).join(', ') || 'none'}`);
          }
        } else {
          // No topping selected - this is expected and OK
          // console.log('No topping selected for this cake');
        }

        // Check if we have all required images
        if (!shapeImageUrl || !flavorImageUrl || !shapeColorImageUrl) {
          setPreviewImage(null);
          setIsLoading(false);
          return;
        }

        // Merge images - always generate fresh, no caching
        const mergedImage = await mergeImages(
          getAssetURl(shapeImageUrl, assetUrl),
          getAssetURl(flavorImageUrl, assetUrl),
          cake.colorCode,
          toppingImageUrl ? getAssetURl(toppingImageUrl, assetUrl) : undefined,
          getAssetURl(shapeColorImageUrl, assetUrl)
        );

        // Only update if we got a valid merged image
        if (mergedImage && mergedImage.startsWith('data:image/')) {
          setPreviewImage(mergedImage);
        } else {
          setPreviewImage(null);
        }
      } catch (error) {
        console.error('Error generating preview image:', error);
        setPreviewImage(null);
      } finally {
        setIsLoading(false);
      }
    };

    generatePreview();
  }, [cakeKey, cake, shapes, flavors, toppingItems, assetUrl]); // Regenerate when any dependency changes

  return { previewImage, isLoading };
};

